import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, BookOpen, User, MapPin, Search, Grid, List } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface FavoriteBook {
  id: string;
  created_at: string;
  books: {
    id: string;
    title: string;
    author: string;
    cover_image?: string;
    is_available: boolean;
    owner_id: string;
    users: {
      name: string;
      city: string;
    };
  };
}

const Favorites: React.FC = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/favorites');
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteToggle = (bookId: string, isFavorited: boolean, newCount: number) => {
    if (!isFavorited) {
      // Remove from favorites list
      setFavorites(prev => prev.filter(fav => fav.books.id !== bookId));
    }
  };

  const filteredFavorites = favorites.filter(favorite =>
    favorite.books.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    favorite.books.author.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatOwnerName = (fullName: string) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-stone-200 border-t-forest-600 mx-auto mb-4"></div>
          <p className="text-ink-muted">Chargement de vos favoris…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <div className="flex items-center space-x-3 mb-2">
                <Heart className="h-7 w-7 text-forest-700" />
                <h1 className="font-serif text-3xl sm:text-4xl text-ink">
                  Mes favoris
                </h1>
              </div>
              <p className="text-base sm:text-lg text-gray-600">
                {favorites.length} livre{favorites.length > 1 ? 's' : ''} dans vos favoris
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search and View Mode */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm sm:text-base"
                  placeholder="Rechercher dans vos favoris..."
                />
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-forest-100 text-forest-700' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-forest-100 text-forest-700' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Favorites Grid/List */}
        {filteredFavorites.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 max-w-md mx-auto">
              <Heart className="mx-auto h-16 w-16 text-gray-300 mb-6" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery ? 'Aucun favori trouvé' : 'Aucun favori pour le moment'}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery 
                  ? 'Aucun livre ne correspond à votre recherche dans vos favoris.'
                  : 'Commencez par ajouter des livres à vos favoris en cliquant sur le cœur.'
                }
              </p>
              {!searchQuery && (
                <Link to="/library" className="btn btn-primary">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Explorer la bibliothèque
                </Link>
              )}
            </div>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                {filteredFavorites.map((favorite) => (
                  <div key={favorite.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                    <Link to={`/book/${favorite.books.id}`} className="block relative">
                      <div className="aspect-w-3 aspect-h-4">
                        {favorite.books.cover_image ? (
                          <img
                            src={favorite.books.cover_image}
                            alt={favorite.books.title}
                            className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <BookOpen className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* Indicateur de disponibilité */}
                      {favorite.books.is_available ? (
                        <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                          Disponible
                        </div>
                      ) : (
                        <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                          Non disponible
                        </div>
                      )}
                    </Link>
                    
                    <div className="p-4 sm:p-5">
                      <Link to={`/book/${favorite.books.id}`} className="block">
                        <h3 className="font-semibold text-gray-900 truncate hover:text-forest-700 transition-colors mb-2 h-8 flex items-center" title={favorite.books.title}>
                          {favorite.books.title}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600 mb-3">
                        {favorite.books.author}
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <User className="h-4 w-4 mr-2" />
                          <span className="font-medium">{formatOwnerName(favorite.books.users.name)}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="h-4 w-4 mr-2" />
                          <span>{favorite.books.users.city}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          favorite.books.is_available 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {favorite.books.is_available ? 'Disponible' : 'Non disponible'}
                        </span>
                        
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>Ajouté le {formatDate(favorite.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {filteredFavorites.map((favorite) => (
                  <div key={favorite.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-2 sm:space-x-6">
                      <Link to={`/book/${favorite.books.id}`} className="flex-shrink-0 relative">
                        {favorite.books.cover_image ? (
                          <img
                            src={favorite.books.cover_image}
                            alt={favorite.books.title}
                            className="w-12 h-16 sm:w-16 sm:h-20 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                            <BookOpen className="h-4 w-4 sm:h-6 sm:w-6 text-gray-400" />
                          </div>
                        )}
                        
                        {/* Indicateur de disponibilité */}
                        {favorite.books.is_available ? (
                          <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-green-600 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium shadow-lg">
                            ✓
                          </div>
                        ) : (
                          <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-red-600 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium shadow-lg">
                            ✗
                          </div>
                        )}
                      </Link>
                      
                      <div className="flex-1 min-w-0 pr-2">
                        <Link to={`/book/${favorite.books.id}`} className="block">
                          <h3 className="font-semibold text-gray-900 hover:text-forest-700 transition-colors mb-1 text-sm sm:text-base line-clamp-2 leading-tight">
                            {favorite.books.title}
                          </h3>
                        </Link>
                        <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-1 leading-tight">
                          {favorite.books.author}
                        </p>
                        
                        <div className="space-y-2 mb-2">
                          <div className="flex flex-col sm:flex-row sm:items-center text-xs sm:text-sm text-gray-500 space-y-1 sm:space-y-0">
                            <div className="flex items-center">
                              <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              <span className="font-medium truncate">{formatOwnerName(favorite.books.users.name)}</span>
                            </div>
                            <div className="flex items-center">
                              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              <span className="truncate">{favorite.books.users.city}</span>
                            </div>
                          </div>
                        </div>
                        
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          favorite.books.is_available 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {favorite.books.is_available ? 'Disponible' : 'Non disponible'}
                        </span>
                      </div>
                      
                      <div className="flex flex-col items-end text-xs text-gray-400">
                        <span>Ajouté le</span>
                        <span>{formatDate(favorite.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Favorites;
